/**
 * Sidebar — Panel lateral derecho con controles y atajos.
 *
 * Los botones invocan métodos en `window.app` (la instancia de
 * ClinicalGraphTriageApp) mediante `onclick=app.xxx()`. El patrón existe
 * porque el sidebar es HTML estático; mover callbacks a addEventListener
 * tipados es una mejora deferida — por ahora el objetivo es sacar el HTML
 * pesado de main.ts.
 */

import { authService } from '../auth/AuthService';
import { teacherDashboard } from '../education/TeacherDashboard';
import { getLocale, setLocale, type Locale } from '../i18n';

/** Expone API global para colapsar/expandir desde main.ts. */
export interface SidebarAPI {
  collapse(): void;
  expand(): void;
  toggle(): void;
  isCollapsed(): boolean;
  /** Actualiza el banner "Caso activo" en el sidebar. Llamar desde loadPatient. */
  setActiveCase(label: string | null, triage?: string): void;
}

export function mountSidebar(): SidebarAPI {
  // Toggle flotante siempre visible (incluso cuando sidebar colapsado).
  const toggle = document.createElement('button');
  toggle.id = 'cgt-sidebar-toggle';
  toggle.setAttribute('aria-label', 'Mostrar/ocultar panel');
  toggle.textContent = '≡';
  document.body.appendChild(toggle);

  const sidebar = document.createElement('div');
  sidebar.className = 'cgt-sidebar';
  sidebar.innerHTML = `
    <h3 style="margin: 0 0 4px 0; font-size: 16px; display: flex; align-items: center; gap: 10px; color: #f5f5f7; font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif; font-weight: 700;">
      <img src="/logo.svg" alt="" width="28" height="28" style="display:block;border-radius:7px;box-shadow:0 2px 8px rgba(41,151,255,0.3);">
      <span>Dr Newen <span style="background:linear-gradient(90deg,#2997FF,#BF5AF2);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;">IA</span></span>
    </h3>
    <div style="font-size: 10px; color: #6e6e73; letter-spacing: 1px; text-transform: uppercase; margin-bottom: 16px;">Clinical Triage System</div>

    <!-- CASO ACTIVO (banner compacto + botón cambiar) -->
    <div id="cgt-active-case" style="margin-bottom: 14px; padding: 12px; border-radius: 10px; background: rgba(41,151,255,0.08); border: 0.5px solid rgba(41,151,255,0.25);">
      <div style="font-size: 10px; color: #6e6e73; letter-spacing: 1px; text-transform: uppercase; margin-bottom: 4px;">Caso activo</div>
      <div data-active-label style="font-size: 13px; font-weight: 600; color: #f5f5f7; line-height: 1.3;">—</div>
      <div data-active-triage style="font-size: 10px; margin-top: 4px; font-family: 'JetBrains Mono', monospace;"></div>
      <button data-toggle-case-picker style="margin-top: 10px; width: 100%; padding: 6px; background: transparent; color: #2997FF; border: 0.5px solid rgba(41,151,255,0.35); border-radius: 6px; cursor: pointer; font-size: 11px; font-weight: 600;">↺ Cambiar caso</button>
    </div>

    <!-- SELECTOR DE CASO (colapsado por default) -->
    <div id="cgt-case-picker" style="display: none; margin-bottom: 20px; padding: 12px; border-radius: 10px; background: rgba(255,255,255,0.02); border: 0.5px dashed rgba(255,255,255,0.1);">
      <div style="font-size: 10px; color: #888; letter-spacing: 0.5px; text-transform: uppercase; margin-bottom: 8px;">Elegí un caso</div>
      <button onclick="app.startScenario('iam', 'individual')" style="width: 100%; padding: 10px; margin-bottom: 6px; background: linear-gradient(135deg, #FF453A, #FF6B6B); color: white; border: none; border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 12px;">🫀 IAM (Infarto agudo)</button>
      <button onclick="app.startScenario('sepsis', 'individual')" style="width: 100%; padding: 10px; margin-bottom: 6px; background: linear-gradient(135deg, #8B5CF6, #C084FC); color: white; border: none; border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 12px;">🦠 Sepsis</button>
      <button onclick="app.startScenario('epoc', 'individual')" style="width: 100%; padding: 10px; margin-bottom: 8px; background: linear-gradient(135deg, #10B981, #34D399); color: white; border: none; border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 12px;">🫁 EPOC exacerbado</button>

      <div style="font-size: 10px; color: #888; margin: 10px 0 6px;">🎲 Variaciones aleatorias</div>
      <div style="display: flex; gap: 4px; margin-bottom: 8px;">
        <button onclick="app.startRandomScenario('iam')"    style="flex: 1; padding: 6px; background: rgba(255,69,58,0.15); color: #FF8A80; border: 0.5px solid rgba(255,69,58,0.3); border-radius: 6px; cursor: pointer; font-size: 10px; font-weight: 600;">IAM</button>
        <button onclick="app.startRandomScenario('sepsis')" style="flex: 1; padding: 6px; background: rgba(139,92,246,0.15); color: #C084FC; border: 0.5px solid rgba(139,92,246,0.3); border-radius: 6px; cursor: pointer; font-size: 10px; font-weight: 600;">Sepsis</button>
        <button onclick="app.startRandomScenario('epoc')"   style="flex: 1; padding: 6px; background: rgba(16,185,129,0.15); color: #69E894; border: 0.5px solid rgba(16,185,129,0.3); border-radius: 6px; cursor: pointer; font-size: 10px; font-weight: 600;">EPOC</button>
      </div>

      <button onclick="app.loadCustomCase('dka')" title="Caso personalizado cargado desde JSON externo" style="width: 100%; padding: 8px; background: rgba(249,115,22,0.12); color: #F97316; border: 0.5px dashed rgba(249,115,22,0.4); border-radius: 6px; cursor: pointer; font-size: 11px;">+ DKA (ejemplo JSON)</button>
    </div>

    <div class="cgt-sb-graph-only" style="margin-bottom: 20px;">
      <h4 style="font-size: 12px; color: #888; margin-bottom: 8px;">TOPOLOGÍA</h4>
      <div style="display: flex; gap: 4px; flex-wrap: wrap;">
        <button onclick="app.setTopology(0)" style="flex: 1; padding: 6px; background: #333; color: white; border: none; border-radius: 4px; font-size: 11px; cursor: pointer;">1:Anatómico</button>
        <button onclick="app.setTopology(1)" style="flex: 1; padding: 6px; background: #333; color: white; border: none; border-radius: 4px; font-size: 11px; cursor: pointer;">2:Fisio</button>
        <button onclick="app.setTopology(2)" style="flex: 1; padding: 6px; background: #333; color: white; border: none; border-radius: 4px; font-size: 11px; cursor: pointer;">3:Temporal</button>
        <button onclick="app.setTopology(3)" style="flex: 1; padding: 6px; background: #333; color: white; border: none; border-radius: 4px; font-size: 11px; cursor: pointer;">4:Diag</button>
      </div>
    </div>

    <div style="margin-bottom: 20px;">
      <h4 style="font-size: 12px; color: #888; margin-bottom: 8px;">HERRAMIENTAS</h4>
      <button onclick="app.openModeSelector()" title="Elegir entre Triage de sala, Monitor o Grafo" style="width: 100%; padding: 10px; margin-bottom: 6px; background: linear-gradient(135deg, #2997FF, #BF5AF2); color: white; border: none; border-radius: 6px; cursor: pointer; font-weight: 700;">🔄 Cambiar modo</button>
      <button onclick="app.toggleMonitor()" title="Monitor multiparamétrico hospitalario" style="width: 100%; padding: 10px; margin-bottom: 6px; background: linear-gradient(135deg, #0a1428, #1a2e4a); color: #30D158; border: 0.5px solid rgba(48,209,88,0.35); border-radius: 6px; cursor: pointer; font-weight: 600;">🩺 Monitor hospitalario</button>
      <button onclick="app.openMassTriage(6)" title="Sala de urgencias con múltiples pacientes" style="width: 100%; padding: 10px; margin-bottom: 6px; background: linear-gradient(135deg, #2a0a0a, #4a1a1a); color: #FF6961; border: 0.5px solid rgba(255,69,58,0.35); border-radius: 6px; cursor: pointer; font-weight: 600;">🚑 Mass triage</button>
      <button onclick="app.replayBriefing()" title="Re-abrir la consigna del caso" style="width: 100%; padding: 10px; margin-bottom: 6px; background: rgba(41,151,255,0.15); color: #2997FF; border: 0.5px solid rgba(41,151,255,0.3); border-radius: 6px; cursor: pointer; font-weight: 600;">📖 Ver briefing</button>
      <button onclick="app.startDifferential()" style="width: 100%; padding: 10px; margin-bottom: 6px; background: #333; color: white; border: none; border-radius: 6px; cursor: pointer; font-weight: 600;">📊 Panel diferencial</button>
      <button onclick="app.downloadCertificate()" title="Solo disponible después de un diagnóstico correcto" style="width: 100%; padding: 10px; margin-bottom: 6px; background: rgba(48,209,88,0.12); color: #30D158; border: 0.5px solid rgba(48,209,88,0.3); border-radius: 6px; cursor: pointer; font-weight: 600;">📄 Descargar certificado</button>
      <button onclick="app.startQuiz()" style="width: 100%; padding: 10px; margin-bottom: 6px; background: #333; color: white; border: none; border-radius: 6px; cursor: pointer; font-weight: 600;">🧩 Quiz rápido</button>
      <button onclick="app.startTimeline()" style="width: 100%; padding: 10px; margin-bottom: 6px; background: #333; color: white; border: none; border-radius: 6px; cursor: pointer; font-weight: 600;">⏱ Evolución temporal</button>
      <button onclick="app.startTutorial()" style="width: 100%; padding: 10px; margin-bottom: 6px; background: #333; color: white; border: none; border-radius: 6px; cursor: pointer; font-weight: 600;">📖 Tutorial guiado</button>
    </div>

    <div style="margin-bottom: 20px;">
      <h4 style="font-size: 12px; color: #888; margin-bottom: 8px;">🧠 ASISTENTE IA</h4>
      <button id="ai-analyze-btn" onclick="app.openAIAssistant('diagnostico')" title="Diagnósticos diferenciales, triage recomendado y exámenes sugeridos (Kimi K2.5)" style="width: 100%; padding: 10px; margin-bottom: 6px; background: linear-gradient(135deg, #2997FF, #BF5AF2); color: white; border: none; border-radius: 6px; cursor: pointer; font-weight: 600; display: flex; align-items: center; justify-content: center; gap: 8px;">
        <span>🧠</span> Abrir asistente
      </button>
      <div style="display: flex; gap: 4px; margin-bottom: 4px;">
        <button onclick="app.openAIAssistant('diagnostico')" title="Diagnósticos diferenciales" style="flex: 1; padding: 7px 4px; background: rgba(41,151,255,0.12); color: #2997FF; border: 0.5px solid rgba(41,151,255,0.3); border-radius: 6px; cursor: pointer; font-size: 10.5px; font-weight: 600;">Dx</button>
        <button onclick="app.openAIAssistant('patrones')" title="Patrones clínicos similares (pgvector)" style="flex: 1; padding: 7px 4px; background: rgba(191,90,242,0.12); color: #C084FC; border: 0.5px solid rgba(191,90,242,0.3); border-radius: 6px; cursor: pointer; font-size: 10.5px; font-weight: 600;">Patrones</button>
        <button onclick="app.openAIAssistant('tutor')" title="Tutor socrático" style="flex: 1; padding: 7px 4px; background: rgba(48,209,88,0.12); color: #30D158; border: 0.5px solid rgba(48,209,88,0.3); border-radius: 6px; cursor: pointer; font-size: 10.5px; font-weight: 600;">Tutor</button>
      </div>
      <div id="ai-status" style="font-size: 10px; color: #666; margin-top: 6px; text-align: center;">
        Configurando...
      </div>
    </div>

    <div style="margin-bottom: 20px;">
      <h4 style="font-size: 12px; color: #888; margin-bottom: 8px;">CÁMARA</h4>
      <div id="cgt-camera-mode" style="display: flex; gap: 4px; margin-bottom: 8px;">
        <button data-cam="background" style="flex: 1; padding: 6px; background: #333; color: white; border: none; border-radius: 4px; font-size: 10px; cursor: pointer;">AR</button>
        <button data-cam="pip" style="flex: 1; padding: 6px; background: #333; color: white; border: none; border-radius: 4px; font-size: 10px; cursor: pointer;">PiP</button>
        <button data-cam="off" style="flex: 1; padding: 6px; background: #333; color: white; border: none; border-radius: 4px; font-size: 10px; cursor: pointer;">Off</button>
      </div>
      <h4 style="font-size: 12px; color: #888; margin-bottom: 8px; margin-top: 12px;">GESTOS</h4>
      <div style="font-size: 11px; color: #aaa; line-height: 1.6;">
        <strong style="color:#c7c7cc">Grafo 3D:</strong><br>
        👌 Pinch: Examinar nodo<br>
        ✊ Puño: Arrastrar<br>
        🙌 Dos manos: Zoom<br>
        <br>
        <strong style="color:#c7c7cc">Monitor (procedimientos):</strong><br>
        🫸 Dos manos apiladas rítmicas: RCP<br>
        ⚡ Palmas con thrust frontal: Defib<br>
        🫱 Índice + medio extendidos: Pulso
      </div>
    </div>

    <div style="margin-bottom: 12px;">
      <button onclick="app.resetView()" style="width: 100%; padding: 10px; background: #333; color: white; border: none; border-radius: 6px; cursor: pointer; font-weight: 600;">🔄 Resetear vista</button>
    </div>

    <div id="cgt-sidebar-user" style="margin-bottom: 16px;"></div>

    <div style="margin-bottom: 16px;">
      <h4 style="font-size: 12px; color: #888; margin-bottom: 8px;">IDIOMA</h4>
      <div id="cgt-locale-switch" style="display: flex; gap: 4px;">
        <button data-locale="es" style="flex:1; padding: 6px; background: #333; color: white; border: none; border-radius: 4px; font-size: 10px; cursor: pointer; font-weight: 600;">ES</button>
        <button data-locale="en" style="flex:1; padding: 6px; background: #333; color: white; border: none; border-radius: 4px; font-size: 10px; cursor: pointer;">EN</button>
      </div>
    </div>

    <div>
      <h4 style="font-size: 12px; color: #888; margin-bottom: 8px;">ATAJOS</h4>
      <div style="font-size: 11px; color: #aaa; line-height: 1.6;">
        1-4: Cambiar modo<br>
        Espacio: Pausar<br>
        H: Ayuda<br>
        C: Reset cámara<br>
        L: Toggle labels<br>
        E: Toggle edges
      </div>
    </div>
  `;
  document.body.appendChild(sidebar);

  renderUserSection(sidebar);
  authService.onChange(() => renderUserSection(sidebar));

  // Toggle del selector de caso (acordeón)
  const casePicker = sidebar.querySelector<HTMLElement>('#cgt-case-picker');
  const toggleCaseBtn = sidebar.querySelector<HTMLButtonElement>('[data-toggle-case-picker]');
  toggleCaseBtn?.addEventListener('click', () => {
    if (!casePicker) return;
    const open = casePicker.style.display === 'block';
    casePicker.style.display = open ? 'none' : 'block';
    toggleCaseBtn.textContent = open ? '↺ Cambiar caso' : '✕ Cerrar selector';
  });
  // Al hacer click en cualquier botón del picker, cerrar el picker después.
  casePicker?.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).tagName === 'BUTTON') {
      setTimeout(() => {
        if (casePicker) casePicker.style.display = 'none';
        if (toggleCaseBtn) toggleCaseBtn.textContent = '↺ Cambiar caso';
      }, 300);
    }
  });

  // Selector de idioma
  const localeBtns = sidebar.querySelectorAll<HTMLButtonElement>('#cgt-locale-switch button[data-locale]');
  const paintLocaleBtns = (locale: Locale): void => {
    localeBtns.forEach(b => {
      const active = b.dataset.locale === locale;
      b.style.background = active ? '#2997FF' : '#333';
      b.style.fontWeight = active ? '600' : '400';
    });
  };
  paintLocaleBtns(getLocale());
  localeBtns.forEach(b => {
    b.addEventListener('click', () => {
      const loc = b.dataset.locale as Locale | undefined;
      if (!loc) return;
      setLocale(loc);
      paintLocaleBtns(loc);
    });
  });

  // Botones de modo cámara
  const savedMode = (localStorage.getItem('cgt_camera_mode') as 'background' | 'pip' | 'off' | null) ?? 'background';
  const cameraBtns = sidebar.querySelectorAll<HTMLButtonElement>('#cgt-camera-mode button[data-cam]');
  const paintCameraBtns = (mode: string): void => {
    cameraBtns.forEach(b => {
      const active = b.dataset.cam === mode;
      b.style.background = active ? '#2997FF' : '#333';
      b.style.fontWeight = active ? '600' : '400';
    });
  };
  paintCameraBtns(savedMode);
  cameraBtns.forEach(b => {
    b.addEventListener('click', () => {
      const mode = b.dataset.cam as 'background' | 'pip' | 'off' | undefined;
      if (!mode) return;
      paintCameraBtns(mode);
      const app = (window as unknown as { app?: { setCameraMode?: (m: string) => Promise<void> } }).app;
      void app?.setCameraMode?.(mode);
    });
  });

  const triageColors: Record<string, string> = {
    immediate: '#FF8A80', delayed: '#FFC671', minor: '#69E894',
    expectant: '#8e8e93', deceased: '#3a3a3c',
  };
  const triageLabels: Record<string, string> = {
    immediate: 'INMEDIATO', delayed: 'PUEDE ESPERAR', minor: 'AMBULATORIO',
    expectant: 'EXPECTANTE', deceased: 'FALLECIDO',
  };

  const api: SidebarAPI = {
    collapse() {
      sidebar.classList.add('collapsed');
      toggle.classList.add('collapsed');
    },
    expand() {
      sidebar.classList.remove('collapsed');
      toggle.classList.remove('collapsed');
    },
    toggle() {
      sidebar.classList.toggle('collapsed');
      toggle.classList.toggle('collapsed');
    },
    isCollapsed() {
      return sidebar.classList.contains('collapsed');
    },
    setActiveCase(label: string | null, triage?: string) {
      const lblEl = sidebar.querySelector<HTMLElement>('[data-active-label]');
      const trgEl = sidebar.querySelector<HTMLElement>('[data-active-triage]');
      if (lblEl) lblEl.textContent = label ?? '—';
      if (trgEl) {
        if (triage && triageLabels[triage]) {
          trgEl.textContent = triageLabels[triage];
          trgEl.style.color = triageColors[triage] ?? '#a1a1a6';
        } else {
          trgEl.textContent = '';
        }
      }
    },
  };
  toggle.addEventListener('click', api.toggle);
  return api;
}

/**
 * Pinta una sección con el usuario actual, atajo al dashboard (si es profesor)
 * y botón de logout. Se actualiza ante onChange de authService.
 */
function renderUserSection(sidebar: HTMLElement): void {
  const container = sidebar.querySelector<HTMLElement>('#cgt-sidebar-user');
  if (!container) return;
  const user = authService.currentUser;
  if (!user) {
    container.innerHTML = '';
    return;
  }

  const isTeacher = user.role === 'teacher';
  container.innerHTML = `
    <div style="padding: 10px 12px; background: rgba(255,255,255,0.03); border-radius: 10px;">
      <div style="font-size: 12px; font-weight: 600; color: #f5f5f7;">${escapeHtml(user.display_name)}</div>
      <div style="font-size: 10px; color: #6e6e73; margin-top: 2px;">${escapeHtml(user.email)}</div>
      <button data-my-progress style="margin-top: 10px; width: 100%; padding: 8px; background: rgba(41,151,255,0.12); color: #2997FF; border: 0.5px solid rgba(41,151,255,0.28); border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 12px;">📈 Mi progreso</button>
      ${isTeacher ? '' : `
        <button data-my-assignments style="margin-top: 6px; width: 100%; padding: 8px; background: rgba(255,159,10,0.12); color: #FF9F0A; border: 0.5px solid rgba(255,159,10,0.28); border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 12px;">📋 Mis tareas</button>
      `}
      ${isTeacher ? `
        <button data-teacher-dashboard style="margin-top: 6px; width: 100%; padding: 8px; background: linear-gradient(135deg, #2997FF, #BF5AF2); color: white; border: none; border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 12px;">📊 Dashboard profesor</button>
        <button data-teacher-assignments style="margin-top: 6px; width: 100%; padding: 8px; background: rgba(255,159,10,0.12); color: #FF9F0A; border: 0.5px solid rgba(255,159,10,0.28); border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 12px;">📋 Asignar casos</button>
        <button data-case-admin style="margin-top: 6px; width: 100%; padding: 8px; background: rgba(255,255,255,0.04); color: #f5f5f7; border: 0.5px solid rgba(255,255,255,0.08); border-radius: 6px; cursor: pointer; font-weight: 500; font-size: 11px;">➕ Crear caso clínico</button>
      ` : ''}
      <a href="/manual/" target="_blank" rel="noopener" style="display:block; margin-top: 6px; width: 100%; padding: 6px; background: transparent; color: #a1a1a6; border: 0.5px solid rgba(255,255,255,0.1); border-radius: 6px; text-decoration: none; text-align: center; font-size: 11px; box-sizing: border-box;">📖 Manual</a>
      <button data-logout style="margin-top: 6px; width: 100%; padding: 6px; background: transparent; color: #a1a1a6; border: 0.5px solid rgba(255,255,255,0.1); border-radius: 6px; cursor: pointer; font-size: 11px;">Cerrar sesión</button>
    </div>
  `;

  container.querySelector<HTMLButtonElement>('[data-my-progress]')
    ?.addEventListener('click', () => {
      const app = (window as unknown as { app?: { openMyProgress?: () => void } }).app;
      app?.openMyProgress?.();
    });
  container.querySelector<HTMLButtonElement>('[data-my-assignments]')
    ?.addEventListener('click', () => {
      const app = (window as unknown as { app?: { openMyAssignments?: () => void } }).app;
      app?.openMyAssignments?.();
    });
  container.querySelector<HTMLButtonElement>('[data-teacher-assignments]')
    ?.addEventListener('click', () => {
      const app = (window as unknown as { app?: { openTeacherAssignments?: () => void } }).app;
      app?.openTeacherAssignments?.();
    });
  container.querySelector<HTMLButtonElement>('[data-teacher-dashboard]')
    ?.addEventListener('click', () => { void teacherDashboard.open(); });
  container.querySelector<HTMLButtonElement>('[data-case-admin]')
    ?.addEventListener('click', () => {
      const app = (window as unknown as { app?: { openCaseAdmin?: () => void } }).app;
      app?.openCaseAdmin?.();
    });
  container.querySelector<HTMLButtonElement>('[data-logout]')
    ?.addEventListener('click', () => {
      authService.logout();
      location.reload();
    });
}

function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c] ?? c));
}

export default mountSidebar;
